import { PaginationButton } from "@/components/atoms/pagination";

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}

/**
 * Generate page numbers to display with ellipsis
 * Shows: [1] [2] [3] ... [10] or [1] ... [5] [6] [7] ... [10]
 */
function getPageNumbers(currentPage: number, totalPages: number): (number | 'ellipsis')[] {
  const pages: (number | 'ellipsis')[] = [];
  const delta = 1; // Number of pages to show on each side of current page
  
  // Always show first page
  pages.push(1);
  
  // Calculate range around current page
  const rangeStart = Math.max(2, currentPage - delta);
  const rangeEnd = Math.min(totalPages - 1, currentPage + delta);
  
  // Add ellipsis after first page if needed
  if (rangeStart > 2) {
    pages.push('ellipsis');
  }
  
  // Add pages around current page
  for (let i = rangeStart; i <= rangeEnd; i++) {
    pages.push(i);
  }
  
  // Add ellipsis before last page if needed
  if (rangeEnd < totalPages - 1) {
    pages.push('ellipsis');
  }
  
  // Always show last page if there's more than one page
  if (totalPages > 1) {
    pages.push(totalPages);
  }
  
  return pages;
}

export function PaginationControls({
  currentPage,
  totalPages,
  onPageChange,
  isLoading = false,
}: PaginationControlsProps) {
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;
  const pageNumbers = getPageNumbers(currentPage, totalPages);

  return (
    <>
      {/* Mobile: Simple prev/next with page info */}
      <div className="flex items-center gap-2 sm:hidden">
        <PaginationButton
          variant="previous"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!canGoPrevious || isLoading}
        />
        <span className="text-sm text-muted-foreground">
          {currentPage} / {totalPages}
        </span>
        <PaginationButton
          variant="next"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!canGoNext || isLoading}
        />
      </div>

      {/* Desktop: Full pagination with page numbers */}
      <div className="hidden items-center gap-1 sm:flex">
        <PaginationButton
          variant="first"
          onClick={() => onPageChange(1)}
          disabled={!canGoPrevious || isLoading}
        />
        <PaginationButton
          variant="previous"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!canGoPrevious || isLoading}
        />
        
        {pageNumbers.map((page, index) => {
          if (page === 'ellipsis') {
            return (
              <span key={`ellipsis-${index}`} className="px-2 text-muted-foreground">
                ...
              </span>
            );
          }
          
          return (
            <PaginationButton
              key={page}
              variant="page"
              onClick={() => onPageChange(page)}
              disabled={isLoading}
              active={currentPage === page}
            >
              {page}
            </PaginationButton>
          );
        })}
        
        <PaginationButton
          variant="next"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!canGoNext || isLoading}
        />
        <PaginationButton
          variant="last"
          onClick={() => onPageChange(totalPages)}
          disabled={!canGoNext || isLoading}
        />
      </div>
    </>
  );
}
